import type { PaginatedResponse } from '@kizen/api';
import type {
  CustomObject,
  CustomObjectDetail,
  CustomObjectField,
  CustomObjectHistory,
  CustomObjectHistoryParams,
  CustomObjectListItem,
  CustomObjectMinimal,
  FieldCategory,
  FilterGroupListItem,
  GetAllCustomObjectsParams,
  GetCustomObjectsParams,
  GetFilterGroupsParams,
  SearchAllFieldsParams,
  SearchFieldsOptions,
} from '@kizen/api/custom-object';
import type { DefaultError, QueryObserverOptions } from '@tanstack/query-core';
import type { AxiosInstance } from 'axios';
import type { Resource } from './resource';
import type { Drop, WithParams, WithRequired } from './types';

import {
  getAllCustomObjectFields,
  getCustomObjectDetail,
  getCustomObjectFields,
  getFilterGroups,
  getCustomObject,
  getCustomObjectCategories,
  getAllCustomObjects,
  getCustomObjectHistory,
  getCustomObjects,
} from '@kizen/api/custom-object';
import { getResource } from './resource';
import { curry1 } from './utils';

type AllCustomObjectFieldsQueryOptions<TData = CustomObjectField[]> = Omit<
  AllCustomObjectFieldsQuery<TData>,
  'queryFn' | 'queryKey'
> & {
  params?: SearchAllFieldsParams;
};

type AllCustomObjectsQueryOptions<TData = CustomObjectMinimal[]> = Omit<
  AllCustomObjectsQuery<TData>,
  'queryFn' | 'queryKey'
> & {
  params?: GetAllCustomObjectsParams;
};

type CustomObjectsQueryOptions<TData = PaginatedResponse<CustomObjectListItem>> =
  Omit<CustomObjectsQuery<TData>, 'queryFn' | 'queryKey'> & {
    params?: GetCustomObjectsParams;
  };

type CustomObjectFieldsQueryOptions<TData = CustomObjectField[]> = Omit<
  CustomObjectFieldsQuery<TData>,
  'queryFn' | 'queryKey'
> & {
  body?: SearchFieldsOptions['body'];
  params?: SearchFieldsOptions['params'];
};

type CustomObjectModelQueryOptions<TData = CustomObject> = Omit<
  CustomObjectModelQuery<TData>,
  'queryFn' | 'queryKey'
>;

type CustomObjectDetailQuery<TData = CustomObjectDetail> = WithRequired<
  QueryObserverOptions<
    CustomObjectDetail,
    DefaultError,
    TData,
    CustomObjectDetail,
    Readonly<[Resource<Readonly<['custom-objects', string, 'detail']>>]>
  >,
  'queryFn' | 'queryKey'
>;

type AllCustomObjectFieldsQuery<TData = CustomObjectField[]> = WithRequired<
  QueryObserverOptions<
    CustomObjectField[],
    DefaultError,
    TData,
    CustomObjectField[],
    [
      Resource<
        Readonly<['custom-objects', string, 'fields', 'settings-search']>,
        SearchAllFieldsParams
      >,
    ]
  >,
  'queryFn' | 'queryKey'
>;

type CustomObjectCategoriesQuery<TData = FieldCategory[]> = WithRequired<
  QueryObserverOptions<
    FieldCategory[],
    DefaultError,
    TData,
    FieldCategory[],
    [Resource<Readonly<['custom-objects', string, 'categories']>>]
  >,
  'queryFn' | 'queryKey'
>;

type CustomObjectFieldsQuery<TData = CustomObjectField[]> = WithRequired<
  QueryObserverOptions<
    CustomObjectField[],
    DefaultError,
    TData,
    CustomObjectField[],
    [
      Resource<
        Readonly<['custom-objects', string, 'fields', 'search']>,
        SearchFieldsOptions['params'],
        SearchFieldsOptions['body']
      >,
    ]
  >,
  'queryFn' | 'queryKey'
>;

type CustomObjectModelQuery<TData = CustomObject> = WithRequired<
  QueryObserverOptions<
    CustomObject,
    DefaultError,
    TData,
    CustomObject,
    [Resource<Readonly<['custom-objects', string]>>]
  >,
  'queryFn' | 'queryKey'
>;

type AllCustomObjectsQuery<TData = CustomObjectMinimal[]> = WithRequired<
  QueryObserverOptions<
    CustomObjectMinimal[],
    DefaultError,
    TData,
    CustomObjectMinimal[],
    [
      Resource<
        Readonly<['custom-objects', 'settings-search']>,
        GetAllCustomObjectsParams
      >,
    ]
  >,
  'queryFn' | 'queryKey'
>;

type CustomObjectsQuery<TData = PaginatedResponse<CustomObjectListItem>> =
  WithRequired<
    QueryObserverOptions<
      PaginatedResponse<CustomObjectListItem>,
      DefaultError,
      TData,
      PaginatedResponse<CustomObjectListItem>,
      [Resource<Readonly<['custom-objects']>, GetCustomObjectsParams>]
    >,
    'queryFn' | 'queryKey'
  >;

type CustomObjectHistoryQuery<TData = CustomObjectHistory> = WithRequired<
  QueryObserverOptions<
    CustomObjectHistory,
    DefaultError,
    TData,
    CustomObjectHistory,
    [
      Resource<
        Readonly<['custom-objects', string, 'history']>,
        CustomObjectHistoryParams
      >,
    ]
  >,
  'queryFn' | 'queryKey'
>;

type CustomObjectFilterGroupsQuery<TData = FilterGroupListItem[]> =
  WithRequired<
    QueryObserverOptions<
      FilterGroupListItem[],
      DefaultError,
      TData,
      FilterGroupListItem[],
      [
        Resource<
          Readonly<['custom-objects', string, 'filter-groups']>,
          GetFilterGroupsParams
        >,
      ]
    >,
    'queryFn' | 'queryKey'
  >;

interface CustomObjectFilterGroupsQueryOptions<TData = FilterGroupListItem[]>
  extends Omit<CustomObjectFilterGroupsQuery<TData>, 'queryKey' | 'queryFn'> {
  params?: GetFilterGroupsParams;
}

export const allCustomObjectFieldsQuery = <TData = CustomObjectField[]>(
  axios: AxiosInstance,
  customObjectId: string,
  options: AllCustomObjectFieldsQueryOptions<TData> = {}
): AllCustomObjectFieldsQuery<TData> => {
  const path = [
    'custom-objects',
    customObjectId,
    'fields',
    'settings-search',
  ] as const;
  const { params, ...queryOptions } = options;
  const resource = getResource(path, { params });

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        signal,
        queryKey: [qk],
      } = context;

      return getAllCustomObjectFields(axios, qk.path[1], {
        params: qk.params,
        signal,
      });
    },
    ...queryOptions,
  };
};

export const allCustomObjectsQuery = <TData = CustomObjectMinimal[]>(
  axios: AxiosInstance,
  options: AllCustomObjectsQueryOptions<TData> = {}
): AllCustomObjectsQuery<TData> => {
  const path = ['custom-objects', 'settings-search'] as const;
  const { params, ...queryOptions } = options;
  const resource = getResource(path, { params });

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        signal,
        queryKey: [qk],
      } = context;

      return getAllCustomObjects(axios, { params: qk.params, signal });
    },
    ...queryOptions,
  };
};

export const customObjectCategoriesQuery = <TData = FieldCategory[]>(
  axios: AxiosInstance,
  customObjectId: string,
  options: Omit<CustomObjectCategoriesQuery<TData>, 'queryFn' | 'queryKey'> = {}
): CustomObjectCategoriesQuery<TData> => {
  const path = ['custom-objects', customObjectId, 'categories'] as const;
  const resource = getResource(path);

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        signal,
        queryKey: [qk],
      } = context;

      return getCustomObjectCategories(axios, qk.path[1], { signal });
    },
    ...options,
  };
};

export const customObjectDetailQuery = <TData = CustomObjectDetail>(
  axios: AxiosInstance,
  customObjectId: string,
  options?: Omit<CustomObjectDetailQuery<TData>, 'queryFn' | 'queryKey'>
): CustomObjectDetailQuery<TData> => {
  const path = ['custom-objects', customObjectId, 'detail'] as const;
  const resource = getResource(path);

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        signal,
        queryKey: [qk],
      } = context;

      return getCustomObjectDetail(axios, qk.path[1], { signal });
    },
    ...options,
  };
};

export const customObjectFieldsQuery = <TData = CustomObjectField[]>(
  axios: AxiosInstance,
  customObjectId: string,
  options: CustomObjectFieldsQueryOptions<TData> = {}
): CustomObjectFieldsQuery<TData> => {
  const path = ['custom-objects', customObjectId, 'fields', 'search'] as const;
  const { body, params, ...queryOptions } = options;
  const resource = getResource(path, { body, params, method: 'POST' });

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        signal,
        queryKey: [qk],
      } = context;

      return getCustomObjectFields(axios, qk.path[1], {
        body: qk.body,
        params: qk.params,
        signal,
      });
    },
    ...queryOptions,
  };
};

export const customObjectModelQuery = <TData = CustomObject>(
  axios: AxiosInstance,
  customObjectId: string,
  options: CustomObjectModelQueryOptions<TData> = {}
): CustomObjectModelQuery<TData> => {
  const path = ['custom-objects', customObjectId] as const;
  const resource = getResource(path);

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        signal,
        queryKey: [qk],
      } = context;

      return getCustomObject(axios, qk.path[1], {
        signal,
      });
    },
    ...options,
  };
};

export const customObjectHistoryQuery = <TData = CustomObjectHistory>(
  axios: AxiosInstance,
  id: string,
  options: WithParams<
    Omit<CustomObjectHistoryQuery<TData>, 'queryFn' | 'queryKey'>,
    CustomObjectHistoryParams
  > = {}
): CustomObjectHistoryQuery<TData> => {
  const path = ['custom-objects', id, 'history'] as const;
  const { params, ...queryOpts } = options;
  const resource = getResource(path, { params });

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        queryKey: [qk],
        signal,
      } = context;

      return getCustomObjectHistory(axios, qk.path[1], {
        params: qk.params,
        signal,
      });
    },
    ...queryOpts,
  };
};

export const customObjectFilterGroupsQuery = <TData = FilterGroupListItem[]>(
  axios: AxiosInstance,
  customObjectId: string,
  options: CustomObjectFilterGroupsQueryOptions<TData> = {}
): CustomObjectFilterGroupsQuery<TData> => {
  const { params, ...queryOpts } = options;
  const path = ['custom-objects', customObjectId, 'filter-groups'] as const;
  const resource = getResource(path, { params });

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        queryKey: [qk],
        signal,
      } = context;

      return getFilterGroups(axios, qk.path[1], { params: qk.params, signal });
    },
    ...queryOpts,
  };
};

export const customObjectsQuery = <
  TData = PaginatedResponse<CustomObjectListItem>,
>(
  axios: AxiosInstance,
  options: CustomObjectsQueryOptions<TData> = {}
): CustomObjectsQuery<TData> => {
  const { params, ...queryOpts } = options;
  const path = ['custom-objects'] as const;
  const resource = getResource(path, { params });

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        queryKey: [qk],
        signal,
      } = context;

      return getCustomObjects(axios, { params: qk.params, signal });
    },
    ...queryOpts,
  };
};

class Queries {
  public allCustomObjectsQuery: <TData = CustomObjectMinimal[]>(
    ...args: Drop<Parameters<typeof allCustomObjectsQuery<TData>>>
  ) => ReturnType<typeof allCustomObjectsQuery<TData>>;
  public allCustomObjectFieldsQuery: <TData = CustomObjectField[]>(
    ...args: Drop<Parameters<typeof allCustomObjectFieldsQuery<TData>>>
  ) => ReturnType<typeof allCustomObjectFieldsQuery<TData>>;
  public customObjectCategoriesQuery: <TData = FieldCategory[]>(
    ...args: Drop<Parameters<typeof customObjectCategoriesQuery<TData>>>
  ) => ReturnType<typeof customObjectCategoriesQuery<TData>>;
  public customObjectDetailQuery: <TData = CustomObjectDetail>(
    ...args: Drop<Parameters<typeof customObjectDetailQuery<TData>>>
  ) => ReturnType<typeof customObjectDetailQuery<TData>>;
  public customObjectFieldsQuery: <TData = CustomObjectField[]>(
    ...args: Drop<Parameters<typeof customObjectFieldsQuery<TData>>>
  ) => ReturnType<typeof customObjectFieldsQuery<TData>>;
  public customObjectFilterGroupsQuery: <TData = FilterGroupListItem[]>(
    ...args: Drop<Parameters<typeof customObjectFilterGroupsQuery<TData>>>
  ) => ReturnType<typeof customObjectFilterGroupsQuery<TData>>;
  public customObjectModelQuery: <TData = CustomObject>(
    ...args: Drop<Parameters<typeof customObjectModelQuery<TData>>>
  ) => ReturnType<typeof customObjectModelQuery<TData>>;
  public customObjectHistoryQuery: <TData = CustomObjectHistory>(
    ...args: Drop<Parameters<typeof customObjectHistoryQuery<TData>>>
  ) => CustomObjectHistoryQuery<TData>;
  public customObjectsQuery: <TData = PaginatedResponse<CustomObjectListItem>>(
    ...args: Drop<Parameters<typeof customObjectsQuery<TData>>>
  ) => CustomObjectsQuery<TData>;

  constructor(axios: AxiosInstance) {
    this.allCustomObjectsQuery = curry1(allCustomObjectsQuery)(axios);
    this.allCustomObjectFieldsQuery = curry1(allCustomObjectFieldsQuery)(axios);
    this.customObjectCategoriesQuery = curry1(customObjectCategoriesQuery)(
      axios
    );
    this.customObjectDetailQuery = curry1(customObjectDetailQuery)(axios);
    this.customObjectFieldsQuery = curry1(customObjectFieldsQuery)(axios);
    this.customObjectFilterGroupsQuery = curry1(customObjectFilterGroupsQuery)(
      axios
    );
    this.customObjectModelQuery = curry1(customObjectModelQuery)(axios);
    this.customObjectHistoryQuery = curry1(customObjectHistoryQuery)(axios);
    this.customObjectsQuery = curry1(customObjectsQuery)(axios);
  }
}

export type CustomObjectQueries = Queries;

export const defCustomObjectQueries = (
  axios: AxiosInstance
): CustomObjectQueries => {
  return new Queries(axios);
};
